import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import type { Feature } from 'geojson';

import { EMPTY_MAP_DATA, isRegion, regionCoordinates } from '@atom/map';
import { MapData, MapIdleEvent } from '@atom/types/map';
import { Schema } from '@atom/types/schema';

import {
  LayerFilterKey,
  LayerToggleKey,
  mapLayersReducer,
  MapLayersState,
} from './layers/mapLayersReducer';
import {
  DEFAULT_LAYERS_BASE,
  readInitialLayers,
  writeAtomMapLayers,
} from './layers/mapLayersStorage';
import { applyExclusions, countExcludedInShape } from './exclusionUtilities';
import useMapData from './useMapData';

type MapPortalDataContextValue = {
  data: MapData;
  loading: boolean;
  layers: MapLayersState;
  schemas: Schema[];
  setLayer: (key: LayerToggleKey, value: boolean) => void;
  setFilter: (key: LayerFilterKey, value: Set<string>) => void;
  setRegionFeature: (feature: Feature | null) => void;
  setViewport: (viewport: MapIdleEvent | null) => void;
  hasRegion: boolean;
  excludedCount: number;
  excludeAsset: (id: string) => void;
};

const FALLBACK_LAYERS: MapLayersState = {
  ...DEFAULT_LAYERS_BASE,
  assetSchemas: new Set(),
  clusterDensity: 'default',
};

const MapPortalDataContext = createContext<MapPortalDataContextValue>({
  data: EMPTY_MAP_DATA,
  loading: false,
  layers: FALLBACK_LAYERS,
  schemas: [],
  setLayer: () => {},
  setFilter: () => {},
  setRegionFeature: () => {},
  setViewport: () => {},
  hasRegion: false,
  excludedCount: 0,
  excludeAsset: () => {},
});

export const useMapPortalData = (): MapPortalDataContextValue =>
  useContext(MapPortalDataContext);

type MapLayersContextValue = {
  layers: MapLayersState;
  schemas: Schema[];
  setLayer: (key: LayerToggleKey, value: boolean) => void;
  setFilter: (key: LayerFilterKey, value: Set<string>) => void;
};

export const useMapLayers = (): MapLayersContextValue => {
  const { layers, schemas, setLayer, setFilter } =
    useContext(MapPortalDataContext);
  return { layers, schemas, setLayer, setFilter };
};

type MapPortalDataProviderProps = {
  children: ReactNode;
  schemas: Schema[];
  schemasLoading?: boolean;
  clusterDensity?: string;
};

const MapPortalDataProvider = ({
  children,
  schemas,
  schemasLoading = false,
  clusterDensity = 'default',
}: MapPortalDataProviderProps) => {
  const allSchemaIds = useMemo(() => schemas.map(({ id }) => id), [schemas]);
  const [initial] = useState(() => readInitialLayers(clusterDensity));
  const [layers, dispatch] = useReducer(mapLayersReducer, initial.layers);

  // A user who has never picked their inventory layers gets every schema
  // selected. Everyone else keeps exactly what they stored.
  const [seeded, setSeeded] = useState(!initial.firstRun);

  useEffect(() => {
    if (seeded || schemasLoading) {
      return;
    }

    if (allSchemaIds.length > 0) {
      dispatch({
        type: 'SET_FILTER',
        key: 'assetSchemas',
        value: new Set(allSchemaIds),
      });
    }

    setSeeded(true);
  }, [seeded, schemasLoading, allSchemaIds]);

  useEffect(() => {
    if (!seeded) {
      return;
    }

    writeAtomMapLayers(layers);
  }, [layers, seeded]);

  const setLayer = useCallback(
    (key: LayerToggleKey, value: boolean) =>
      dispatch({ type: 'SET_LAYER', key, value }),
    [],
  );

  const setFilter = useCallback(
    (key: LayerFilterKey, value: Set<string>) =>
      dispatch({ type: 'SET_FILTER', key, value }),
    [],
  );

  const [regionFeature, setRegionFeatureState] = useState<Feature | null>(null);
  const [viewport, setViewport] = useState<MapIdleEvent | null>(null);

  // Assets excluded from the drawn shape. They stay excluded while the shape
  // is moved or reshaped, and come back once the shape is cleared (which also
  // happens on every tool switch).
  const [excludedIds, setExcludedIds] = useState<string[]>([]);

  const setRegionFeature = useCallback((feature: Feature | null) => {
    setRegionFeatureState(feature);

    if (feature === null) {
      setExcludedIds([]);
    }
  }, []);

  const excludeAsset = useCallback(
    (id: string) =>
      setExcludedIds(prev => (prev.includes(id) ? prev : [...prev, id])),
    [],
  );

  const hasRegion = isRegion(regionFeature);
  const coordinates = hasRegion
    ? regionCoordinates(regionFeature)
    : (viewport?.coordinates ?? null);

  const [rawData, loading] = useMapData(layers, {
    coordinates,
    zoom: viewport?.zoom ?? null,
  });

  const data = useMemo(
    () => applyExclusions(rawData, excludedIds),
    [rawData, excludedIds],
  );

  const excludedCount = useMemo(
    () => countExcludedInShape(rawData, excludedIds),
    [rawData, excludedIds],
  );

  const value = useMemo(
    () => ({
      data,
      loading,
      layers,
      schemas,
      setLayer,
      setFilter,
      setRegionFeature,
      setViewport,
      hasRegion,
      excludedCount,
      excludeAsset,
    }),
    [
      data,
      loading,
      layers,
      schemas,
      setLayer,
      setFilter,
      setRegionFeature,
      hasRegion,
      excludedCount,
      excludeAsset,
    ],
  );

  return (
    <MapPortalDataContext.Provider value={value}>
      {children}
    </MapPortalDataContext.Provider>
  );
};

export default MapPortalDataProvider;
